import React, { forwardRef, createContext, useContext } from 'react';
import { clsx } from 'clsx';

/**
 * Card Component
 *
 * A flexible, polished card component with multiple variants
 * and composable sub-components.
 *
 * @example
 * <Card>
 *   <Card.Header>
 *     <Card.Title>Title</Card.Title>
 *     <Card.Description>Description</Card.Description>
 *   </Card.Header>
 *   <Card.Body>Content goes here</Card.Body>
 *   <Card.Footer>Footer content</Card.Footer>
 * </Card>
 */

const CardContext = createContext({ variant: 'default' });

const cardVariants = {
  default: `
    bg-white
    border border-gray-100
    shadow-card
  `,
  elevated: `
    bg-white
    border border-gray-100
    shadow-soft-md
  `,
  interactive: `
    bg-white
    border border-gray-100
    shadow-card
    transition duration-200 ease-smooth
    hover:shadow-card-hover hover:-translate-y-0.5
    cursor-pointer
    active:translate-y-0 active:shadow-card
  `,
  outlined: `
    bg-white
    border border-gray-200
    shadow-none
  `,
  ghost: `
    bg-transparent
    border-0
    shadow-none
  `,
  muted: `
    bg-gray-50
    border border-gray-100
    shadow-none
  `,
  gradient: `
    bg-gradient-to-br from-wrg-blue-500 to-wrg-blue-600
    text-white
    border-0
    shadow-soft-md
  `,
  glass: `
    bg-white/80
    backdrop-blur-md
    border border-white/20
    shadow-soft-md
  `,
  success: `
    bg-success-50
    border border-success-200
    shadow-none
  `,
  warning: `
    bg-warning-50
    border border-warning-200
    shadow-none
  `,
  error: `
    bg-error-50
    border border-error-200
    shadow-none
  `,
  info: `
    bg-info-50
    border border-info-200
    shadow-none
  `,
};

const cardRadii = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  full: 'rounded-3xl',
};

const cardPaddings = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

const Card = forwardRef(
  (
    {
      children,
      variant = 'default',
      radius = 'md',
      padding = 'none', // Use header/body/footer padding instead
      className,
      as: Component = 'div',
      animate = false,
      ...props
    },
    ref
  ) => {
    return (
      <CardContext.Provider value={{ variant }}>
        <Component
          ref={ref}
          className={clsx(
            'overflow-hidden',
            cardVariants[variant],
            cardRadii[radius],
            padding !== 'none' && cardPaddings[padding],
            animate && 'animate-fade-in-up',
            className
          )}
          {...props}
        >
          {children}
        </Component>
      </CardContext.Provider>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header
 */
const CardHeader = forwardRef(
  ({ children, className, actions, divided = false, ...props }, ref) => {
    const { variant } = useContext(CardContext);
    const isGradient = variant === 'gradient';

    return (
      <div
        ref={ref}
        className={clsx(
          'px-5 py-4',
          divided && !isGradient && 'border-b border-gray-100',
          divided && isGradient && 'border-b border-white/10',
          'flex items-start justify-between gap-4',
          className
        )}
        {...props}
      >
        <div className="flex-1 min-w-0">{children}</div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Title
 */
const CardTitle = forwardRef(
  ({ children, className, as: Component = 'h3', ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={clsx(
          'text-base font-semibold text-gray-900',
          'leading-tight',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CardTitle.displayName = 'CardTitle';

/**
 * Card Description
 */
const CardDescription = forwardRef(
  ({ children, className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={clsx('text-sm text-gray-500 mt-1', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

/**
 * Card Body
 */
const CardBody = forwardRef(
  ({ children, className, noPadding = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(!noPadding && 'px-5 py-4', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

/**
 * Card Footer
 */
const CardFooter = forwardRef(
  ({ children, className, divided = false, align = 'right', ...props }, ref) => {
    const { variant } = useContext(CardContext);
    const isGradient = variant === 'gradient';

    const alignClasses = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'px-5 py-4',
          divided && !isGradient && 'border-t border-gray-100 bg-gray-50/50',
          divided && isGradient && 'border-t border-white/10',
          'flex items-center gap-3',
          alignClasses[align],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

/**
 * Card Image
 */
const CardImage = forwardRef(
  ({ src, alt, className, aspectRatio = 'video', overlay, ...props }, ref) => {
    const aspectRatioClasses = {
      square: 'aspect-square',
      video: 'aspect-video',
      wide: 'aspect-[21/9]',
      portrait: 'aspect-[3/4]',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'relative overflow-hidden',
          aspectRatioClasses[aspectRatio],
          className
        )}
        {...props}
      >
        <img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {overlay && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        )}
      </div>
    );
  }
);

CardImage.displayName = 'CardImage';

/**
 * Card Badge (for status indicators)
 */
const CardBadge = forwardRef(
  ({ children, className, position = 'top-right', ...props }, ref) => {
    const positionClasses = {
      'top-right': 'absolute top-3 right-3',
      'top-left': 'absolute top-3 left-3',
      'bottom-right': 'absolute bottom-3 right-3',
      'bottom-left': 'absolute bottom-3 left-3',
    };

    return (
      <span
        ref={ref}
        className={clsx(positionClasses[position], className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

CardBadge.displayName = 'CardBadge';

/**
 * Stat Card - Specialized card for displaying metrics
 */
const StatCard = forwardRef(
  (
    {
      title,
      subtitle,
      value,
      change,
      trend,
      changeType = 'neutral', // 'positive', 'negative', 'neutral'
      icon: Icon,
      variant = 'default', // 'default', 'primary', 'success', 'warning', 'error', 'info'
      size = 'md', // 'sm', 'md'
      loading = false,
      className,
      ...props
    },
    ref
  ) => {
    const changeColors = {
      positive: 'text-success-600',
      negative: 'text-error-600',
      neutral: 'text-gray-500',
    };

    const variantStyles = {
      default: { bg: 'bg-gray-100', color: 'text-gray-600' },
      primary: { bg: 'bg-wrg-blue-100', color: 'text-wrg-blue-600' },
      success: { bg: 'bg-success-100', color: 'text-success-600' },
      warning: { bg: 'bg-warning-100', color: 'text-warning-600' },
      error: { bg: 'bg-error-100', color: 'text-error-600' },
      info: { bg: 'bg-info-100', color: 'text-info-600' },
    };

    const iconStyle = variantStyles[variant] || variantStyles.default;
    const isSmall = size === 'sm';

    return (
      <Card ref={ref} className={clsx('relative', className)} {...props}>
        <CardBody>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className={clsx(
                'font-medium text-gray-500',
                isSmall ? 'text-xs' : 'text-sm'
              )}>
                {title}
              </p>
              {subtitle && (
                <p className="text-xs text-gray-400">{subtitle}</p>
              )}
              {loading ? (
                <div className={clsx(
                  'bg-gray-200 rounded animate-pulse mt-1',
                  isSmall ? 'h-6 w-16' : 'h-8 w-24'
                )} />
              ) : (
                <p className={clsx(
                  'font-bold text-gray-900 mt-1',
                  isSmall ? 'text-xl' : 'text-2xl'
                )}>
                  {value}
                </p>
              )}
              {change && !loading && (
                <p className={clsx('text-sm mt-1', changeColors[changeType])}>
                  {change}
                </p>
              )}
              {trend && !loading && (
                <p className={clsx(
                  'text-xs mt-1',
                  trend.direction === 'up' ? 'text-success-600' : trend.direction === 'down' ? 'text-error-600' : 'text-gray-500'
                )}>
                  {trend.value} {trend.label}
                </p>
              )}
            </div>
            {Icon && (
              <div
                className={clsx(
                  'rounded-xl flex-shrink-0',
                  isSmall ? 'p-2' : 'p-3',
                  iconStyle.bg
                )}
              >
                <Icon className={clsx(
                  isSmall ? 'w-5 h-5' : 'w-6 h-6',
                  iconStyle.color
                )} />
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }
);

StatCard.displayName = 'StatCard';

// Attach sub-components
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Image = CardImage;
Card.Badge = CardBadge;

export { Card, StatCard, CardHeader, CardTitle, CardDescription, CardBody, CardFooter, CardImage, CardBadge };
export default Card;
